import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import { useSelector } from 'react-redux';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { selectCurrentUser } from '../../redux/slices/authSlice';

/**
 * Benachrichtigungs-Präferenzen pro Portal-Nutzer und Event-Typ.
 * Gespeichert in users/{uid}.notificationPreferences und serverseitig
 * von den Cloud Functions ausgewertet (createPortalRideNotifications):
 *   'all'  = Glocke + Web-Push
 *   'bell' = nur Glocke, kein Web-Push
 *   'off'  = keine Benachrichtigung
 */

// Muss mit PORTAL_EVENT_DEFAULT_CHANNELS in den Cloud Functions übereinstimmen
const DEFAULT_CHANNELS = {
  portal_dispatcher_assignment_accepted: 'bell'
};

const EVENT_GROUPS = [
  {
    label: 'Neue Bestellungen',
    events: [
      { key: 'portal_new_instant_ride', label: 'Neue Sofortbestellung', description: 'Kunden-App, PayPal oder Telefon-KI' },
      { key: 'portal_new_preorder', label: 'Neue Vorbestellung', description: 'Kunden-App oder Telefon-KI' }
    ]
  },
  {
    label: 'Stornierungen',
    events: [
      { key: 'portal_ride_cancelled_by_customer', label: 'Kunde storniert', description: 'Per App oder Telefon-KI' },
      { key: 'portal_ride_auto_cancelled', label: 'Automatisch storniert', description: 'Timeout — kein Fahrer gefunden' },
      { key: 'portal_ride_cancelled_by_driver', label: 'Fahrer storniert endgültig', description: 'Fahrgast nicht angetroffen / auf Kundenwunsch' },
      { key: 'portal_preorder_gegenvorschlag_expired', label: 'Gegenvorschlag abgelaufen', description: 'Vorbestellung dadurch storniert' },
      { key: 'portal_ride_cancelled', label: 'Storniert (Ursache unbekannt)', description: 'Ältere App-Versionen ohne Storno-Details' }
    ]
  },
  {
    label: 'Disposition',
    events: [
      { key: 'portal_dispatch_failed', label: 'Disposition nicht erfolgreich', description: 'Alle Angebote erschöpft oder manuell nötig' },
      { key: 'portal_dispatch_no_candidates', label: 'Kein passendes Fahrzeug', description: 'Z. B. Spezialfähigkeit ohne verfügbares Fahrzeug' },
      { key: 'portal_ride_returned_by_driver', label: 'Fahrer gibt Fahrt zurück', description: 'Fahrt wird automatisch neu disponiert' },
      { key: 'portal_assignment_rejected', label: 'Fahrer lehnt Zuweisung ab', description: 'Antwort auf eine manuelle Zuweisung' },
      { key: 'portal_assignment_mismatch', label: 'Zuweisung passt nicht', description: 'Zugewiesenes Fahrzeug erfüllt Typ oder Spezialfähigkeit nicht' },
      { key: 'portal_dispatcher_assignment_accepted', label: 'Fahrer nimmt Zuweisung an', description: 'Bestätigung der eigenen Zuweisung' }
    ]
  },
  {
    label: 'Vorbestellungen',
    events: [
      { key: 'portal_preorder_returned', label: 'Vorbestellung wieder ohne Fahrer', description: 'Zurückgegeben oder Fahrer offline' },
      { key: 'portal_preorder_unassigned', label: 'Vorbestellung ohne Fahrer', description: 'Kurz vor Abholzeit noch nicht angenommen' },
      { key: 'portal_preorder_not_started', label: 'Vorbestellung nicht angetreten', description: 'Abholzeit erreicht, Fahrt nicht gestartet' }
    ]
  },
  {
    label: 'Krankenfahrten / Verordnungen',
    events: [
      { key: 'portal_medical_quota_low', label: 'Kontingent fast aufgebraucht', description: 'Nur noch wenige Fahrten auf der Verordnung frei' },
      { key: 'portal_medical_quota_exhausted', label: 'Kontingent aufgebraucht', description: 'Alle Fahrten der Verordnung belegt' },
      { key: 'portal_medical_order_expiring', label: 'Verordnung läuft ab', description: 'Gültigkeitsende in wenigen Tagen' },
      { key: 'portal_medical_order_incomplete', label: 'Verordnung unvollständig', description: 'Schnellanlage-Verordnung wartet auf Vervollständigung' }
    ]
  },
  {
    label: 'Rechnungskunden',
    events: [
      { key: 'portal_invoice_customer_incomplete', label: 'Rechnungskunde unvollständig', description: 'Schnellanlage — Anschrift/Rechnungs-E-Mail fehlt noch' }
    ]
  },
  {
    label: 'Fahrer / Dokumente',
    events: [
      { key: 'portal_driver_license_expiring', label: 'P-Schein läuft ab', description: 'Personenbeförderungsschein 30 bzw. 7 Tage vor Ablauf' },
      { key: 'portal_driver_license_expired', label: 'P-Schein abgelaufen', description: 'Personenbeförderungsschein ist abgelaufen' },
      { key: 'portal_driver_auto_logout', label: 'Fahrer automatisch abgemeldet', description: 'App beendet, 3 Warnungen unbeantwortet' }
    ]
  }
];

const CHANNEL_OPTIONS = [
  { value: 'all', label: 'Glocke + Push' },
  { value: 'bell', label: 'Nur Glocke' },
  { value: 'off', label: 'Aus' }
];

const channelForEvent = (preferences, eventKey) => {
  const value = preferences?.[eventKey];
  if (value === 'all' || value === 'bell' || value === 'off') return value;
  return DEFAULT_CHANNELS[eventKey] || 'all';
};

const NotificationPreferencesSection = () => {
  const user = useSelector(selectCurrentUser);
  const [preferences, setPreferences] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const loadPreferences = async () => {
      if (!user?.uid) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!active) return;
        const stored = userDoc.data()?.notificationPreferences;
        setPreferences(stored && typeof stored === 'object' ? stored : {});
      } catch (loadError) {
        if (active) {
          setError('Benachrichtigungs-Einstellungen konnten nicht geladen werden.');
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadPreferences();
    return () => {
      active = false;
    };
  }, [user?.uid]);

  const handleChannelChange = async (eventKey, channel) => {
    if (!user?.uid) return;
    const previous = preferences;
    const next = { ...preferences, [eventKey]: channel };

    setPreferences(next);
    setSavingKey(eventKey);
    setError('');

    try {
      await setDoc(doc(db, 'users', user.uid), { notificationPreferences: next }, { merge: true });
    } catch (saveError) {
      setPreferences(previous);
      setError('Einstellung konnte nicht gespeichert werden.');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <>
      <Box sx={{ p: 2, bgcolor: 'surface.100' }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Benachrichtigungs-Ereignisse
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Persönliche Einstellung (gilt für Glocke und Web-Push auf allen Geräten)
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        EVENT_GROUPS.map((group) => (
          <Box key={group.label}>
            <Box sx={{ px: 2, pt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {group.label}
              </Typography>
            </Box>
            <List disablePadding>
              {group.events.map((event) => (
                <ListItem
                  key={event.key}
                  secondaryAction={
                    <Select
                      size="small"
                      value={channelForEvent(preferences, event.key)}
                      onChange={(changeEvent) => handleChannelChange(event.key, changeEvent.target.value)}
                      disabled={savingKey === event.key}
                      sx={{ minWidth: 150 }}
                    >
                      {CHANNEL_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  }
                >
                  <ListItemText
                    primary={event.label}
                    secondary={event.description}
                    sx={{ pr: 20 }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        ))
      )}
    </>
  );
};

export default NotificationPreferencesSection;
